import React, { useContext, useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router";
import posthog from "posthog-js";
import { useFeatureFlagEnabled } from "posthog-js/react";
import { GPUInstanceType } from "components/Environment/Settings/Tabs/Compute/InstanceChanger/GPUTypes";
import { OrgContext } from "contexts/OrgContext";
import { OneClickDeployContext } from "contexts/OneClickDeployContext";
import { ProcessedLaunchableData, useLaunchable } from "../../utils";
import { getLaunchableQueryParams } from "../shared/utils";
import { DeployPageContent } from "../shared/DeployPageContent";
import { isKasAuthFlow } from "server/kas/utils";
import agent from "server";

const WillHillLaunchableIDMessUp = "env-2hQX3n7ae5mq3NjNZ32DfAG0tJf";

const LaunchableDeploy: React.FC = () => {
  const history = useHistory();
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const { queryLaunchableID } = getLaunchableQueryParams(location.search);
  const {
    launchableData,
    error,
    loading,
    launchableNotFound,
    launchableAccessError,
  } = useLaunchable(queryLaunchableID || "", false);

  const orgContext = useContext(OrgContext);
  const [nameErrorMessage, setErrorMessage] = useState<string>("");
  const [deploymentStarted, setDeploymentStarted] = useState(false);
  const oneClickContext = useContext(OneClickDeployContext);
  const flagNimsHackathonEnabled = useFeatureFlagEnabled("nims-hackathon");

  const [loadingInstance, setLoadingInstance] = useState(false);
  const [currentInstanceTypeObject, setCurrentInstanceTypeObject] =
    useState<GPUInstanceType | null>(null);
  const [currentStorage, setCurrentStorage] = useState<string | null>(
    launchableData?.diskStorage || null
  );

  const [extraEventProperties, setExtraEventProperties] = useState<
    Record<string, any>
  >({});

  const [lifecycleScript, setLifecycleScript] = useState<string | null>(null);
  const [lifecycleScriptLoading, setLifecycleScriptLoading] = useState(false);

  useEffect(() => {
    if (!isKasAuthFlow) {
      const redirectUrl = `/launchable-migrate-notice?${new URLSearchParams({
        redirect: `https://brev.nvidia.com/launchable/deploy/now?${query.toString()}`,
      }).toString()}`;
      history.push(redirectUrl);
    }
  }, [query, history]);

  useEffect(() => {
    if (launchableData?.containerSelected?.vmBuild?.lifeCycleScriptAttr?.id) {
      getLifeCycleScript(
        queryLaunchableID || "",
        launchableData?.containerSelected?.vmBuild?.lifeCycleScriptAttr?.id
      );
    }
  }, [launchableData]);

  const getLifeCycleScript = async (
    envId: string,
    lifecycleScriptId: string
  ) => {
    setLifecycleScriptLoading(true);
    const res = await agent.Workspaces.getLifeCycleScript(
      envId,
      lifecycleScriptId
    );
    setLifecycleScriptLoading(false);
    if (res.data) {
      setLifecycleScript(res.data?.attrs?.script || "");
    }
  };

  const getInstanceFromType = async (
    instanceType: string,
    workspaceGroupID: string
  ): Promise<GPUInstanceType[] | null> => {
    const res = await agent.Instances.getInstanceByType(
      instanceType,
      workspaceGroupID
    );
    if (res.success && res.data && res.data.length > 0) {
      return res.data;
    }
    return null;
  };

  const getInstanceObject = async (
    Instance: string | null,
    WorkspaceGroupID: string | null
  ) => {
    const parsedInstance = parseInstance(Instance);
    if (!parsedInstance || !WorkspaceGroupID) {
      return null;
    }
    setLoadingInstance(true);
    const instanceTypes = await getInstanceFromType(
      parsedInstance.instanceType,
      WorkspaceGroupID
    );
    setLoadingInstance(false);
    if (instanceTypes) {
      setCurrentInstanceTypeObject(instanceTypes[0]);
    }
  };

  useEffect(() => {
    // get initial instance type and storage from launchable data
    getInstanceObject(
      launchableData?.instanceType || null,
      launchableData?.workspaceGroupId || null
    );
  }, [launchableData]);

  const parseInstance = (Instance: string | null) => {
    if (!Instance) {
      return null;
    }
    const [chipTypeOrInstanceType, instanceType] = Instance.split("@");
    if (instanceType) {
      return {
        chipType: chipTypeOrInstanceType,
        instanceType: instanceType,
      };
    } else {
      return {
        instanceType: chipTypeOrInstanceType,
      };
    }
  };

  useEffect(() => {
    const fn = async () => {
      const eventProperties = {
        launchableID: queryLaunchableID,
        launchableInstanceType: launchableData?.instanceType,
        workspaceGroupID: launchableData?.workspaceGroupId,
        launchableCreatedByUserID: launchableData?.userId,
        launchableCreatedByOrgID: launchableData?.orgId,
        launchableRawURL: location.pathname + location.search,
      };
      setExtraEventProperties(eventProperties);
      posthog.capture("Launchable Viewed", eventProperties);
    };
    if (!!launchableData) {
      fn();
    }
  }, [launchableData]);

  const isAllowedBaseImage = useMemo(() => {
    if (!launchableData?.containerSelected.verbBuild?.containerUrl) {
      return true;
    }

    if (
      !flagNimsHackathonEnabled &&
      launchableData?.containerSelected?.verbBuild?.containerUrl ===
        "nvcr.io/mphexwv2ysej/meta-llama3-8b-instruct:24.05.rc7"
    ) {
      return false;
    }

    return true;
  }, [flagNimsHackathonEnabled, launchableData?.containerSelected]);

  const handleDeploy = (
    launchableData: ProcessedLaunchableData | null,
    currentInstanceTypeObject: GPUInstanceType | null,
    currentStorage: string | null,
    extraEventProperties: Record<string, any>
  ) => {
    if (queryLaunchableID === WillHillLaunchableIDMessUp) {
      if (launchableData) {
        launchableData?.containerSelected?.verbBuild?.containerUrl ===
          "nvcr.io/nvidia/tensorrt:24.05-py3";
      }
    }
    setDeploymentStarted(true);
    if (launchableData && currentStorage) {
      launchableData.diskStorage = currentStorage || launchableData.diskStorage;
    }
    if (launchableData && currentInstanceTypeObject) {
      launchableData.instanceType =
        currentInstanceTypeObject?.type || launchableData.instanceType;
    }

    if (launchableData?.containerSelected?.vmBuild?.lifeCycleScriptAttr?.id) {
      launchableData.containerSelected.vmBuild.lifeCycleScriptAttr.script =
        lifecycleScript || "";
    }

    oneClickContext.startDeploymentFromLaunchable(
      launchableData,
      extraEventProperties
    );
    posthog.capture("Launchable Deployed", extraEventProperties);
  };

  const handleSetNewCurrentInstanceType = (
    instanceTypeObject: GPUInstanceType | null,
    diskStorage: string
  ) => {
    setCurrentInstanceTypeObject(instanceTypeObject);
    setCurrentStorage(diskStorage);
  };

  return (
    <DeployPageContent
      intendedInstanceType={launchableData?.instanceType || null}
      intendedDiskStorage={launchableData?.diskStorage || ""}
      onSetNewCurrentInstanceType={handleSetNewCurrentInstanceType}
      loading={loading || lifecycleScriptLoading}
      launchableNotFound={launchableNotFound}
      launchableAccessError={launchableAccessError}
      launchableData={launchableData}
      // derived from launchableData or customized by user
      instanceLoading={loadingInstance}
      instanceTypeObject={currentInstanceTypeObject}
      instanceStorage={currentStorage || launchableData?.diskStorage || ""}
      nameErrorMessage={nameErrorMessage}
      isAllowedBaseImage={isAllowedBaseImage}
      deploymentStarted={deploymentStarted}
      isInstancesLoading={orgContext.isInstancesLoading}
      allInstancesAvailable={orgContext.allInstancesAvailable}
      isDeploymentFinished={oneClickContext.isFinished}
      onDeploy={() => {
        handleDeploy(
          launchableData,
          currentInstanceTypeObject,
          currentStorage,
          extraEventProperties
        );
      }}
      workspaceId={oneClickContext.environment?.workspaceId}
      activeOrgId={orgContext.activeOrgId}
      lifecycleScript={lifecycleScript}
    />
  );
};

export default LaunchableDeploy;
